// Palstro offline storage — IndexedDB via a thin wrapper

const DB_NAME = 'palstro-offline'
const DB_VERSION = 1

export type OfflineOp = {
  id?: number
  table: string
  operation: 'insert' | 'update' | 'delete'
  payload: Record<string, unknown>
  created_at: string
  synced: boolean
}

/**
 * Hard allow-list of tables the offline queue may write to.
 *
 * The queue replays raw inserts/updates/deletes (see syncManager.ts), so any
 * table reachable here can be mutated with un-validated client data. We permit
 * only side-effect-free leaf tables; everything else fails closed. Inventory,
 * ledger, order and balance tables are deliberately absent — replaying raw rows
 * against them would corrupt mirrors that must only ever change via RPCs.
 *
 * Per-user preference tables (any name ending in `_preferences`) are also
 * allowed dynamically via isOfflineWriteAllowed().
 */
export const OFFLINE_ALLOWED_TABLES: string[] = [
  'notifications',
  'audit_logs',
  'settings',
  'user_preferences',
  'notification_preferences',
]

/** True only for side-effect-free leaf tables safe to replay raw offline writes. */
export function isOfflineWriteAllowed(table: string): boolean {
  return OFFLINE_ALLOWED_TABLES.includes(table) || table.endsWith('_preferences')
}

let _db: IDBDatabase | null = null

function openDB(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db)
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = e => {
      const db = (e.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains('pending_ops')) {
        const store = db.createObjectStore('pending_ops', { keyPath: 'id', autoIncrement: true })
        store.createIndex('synced', 'synced', { unique: false })
        store.createIndex('table', 'table', { unique: false })
      }
      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache', { keyPath: 'key' })
      }
    }
    req.onsuccess = e => {
      _db = (e.target as IDBOpenDBRequest).result
      resolve(_db)
    }
    req.onerror = () => reject(req.error)
  })
}

/** Queue an operation for later sync */
export async function queueOp(op: Omit<OfflineOp, 'id' | 'synced' | 'created_at'>): Promise<void> {
  if (!isOfflineWriteAllowed(op.table)) {
    throw new Error(
      `Offline queue does not support writes to ${op.table}. Use the appropriate RPC online or contact support.`
    )
  }
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      try {
        const tx = db.transaction('pending_ops', 'readwrite')
        const store = tx.objectStore('pending_ops')
        const req = store.add({ ...op, synced: false, created_at: new Date().toISOString() })
        req.onsuccess = () => resolve()
        req.onerror = () => resolve()
      } catch {
        resolve()
      }
    })
  } catch {
    // IndexedDB unavailable (private browsing, etc.) — silently skip
  }
}

/** Get all unsynced operations */
export async function getPendingOps(): Promise<OfflineOp[]> {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      try {
        const tx = db.transaction('pending_ops', 'readonly')
        const store = tx.objectStore('pending_ops')
        // Fetch all records and filter in JS — IDBKeyRange.only(false) throws
        // a DataError in some browsers because boolean is not a valid IDB key.
        const req = store.getAll()
        req.onsuccess = () => {
          const all = (req.result as OfflineOp[]) ?? []
          resolve(all.filter(op => !op.synced))
        }
        req.onerror = () => resolve([])
      } catch {
        resolve([])
      }
    })
  } catch {
    return []
  }
}

/** Mark an operation as synced */
export async function markSynced(id: number): Promise<void> {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      try {
        const tx = db.transaction('pending_ops', 'readwrite')
        const store = tx.objectStore('pending_ops')
        const getReq = store.get(id)
        getReq.onsuccess = () => {
          const record = getReq.result as OfflineOp
          if (record) {
            record.synced = true
            const putReq = store.put(record)
            putReq.onsuccess = () => resolve()
            putReq.onerror = () => resolve()
          } else {
            resolve()
          }
        }
        getReq.onerror = () => resolve()
      } catch {
        resolve()
      }
    })
  } catch {
    // silently skip
  }
}

/** Cache a value (for offline reads) */
export async function cacheSet(key: string, value: unknown): Promise<void> {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      try {
        const tx = db.transaction('cache', 'readwrite')
        const store = tx.objectStore('cache')
        const req = store.put({ key, value, saved_at: Date.now() })
        req.onsuccess = () => resolve()
        req.onerror = () => resolve()
      } catch {
        resolve()
      }
    })
  } catch {
    // silently skip
  }
}

/** Read a cached value */
export async function cacheGet<T>(key: string): Promise<T | null> {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      try {
        const tx = db.transaction('cache', 'readonly')
        const store = tx.objectStore('cache')
        const req = store.get(key)
        req.onsuccess = () => resolve(req.result ? (req.result.value as T) : null)
        req.onerror = () => resolve(null)
      } catch {
        resolve(null)
      }
    })
  } catch {
    return null
  }
}

/** Count pending (unsynced) ops */
export async function countPending(): Promise<number> {
  const ops = await getPendingOps()
  return ops.length
}
